import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Home from './pages/Home';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { LocalBusinessSchema, BreadcrumbSchema } from './components/StructuredData';
import '@google/model-viewer';
import './App.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Header() {
  const location = useLocation();
  const { lang, setLang, t } = useLanguage();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [lastScrollY, setLastScrollY] = React.useState(0);

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [menuOpen]);

  // Handle scroll reveal
  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // If menu is open, don't hide header
      if (menuOpen) return;

      if (currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false); // Scrolling down
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true); // Scrolling up
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, menuOpen]);

  // Close menu when route changes
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMenuOpen(false);
     
    setIsVisible(true);
  }, [location.pathname]);

  return (
    <header className={`global-header ${!isVisible ? 'header-hidden' : ''} ${menuOpen ? 'menu-open' : ''}`}>
      {/* Overlay to close menu when clicking outside — now inside header stacking context */}
      {menuOpen && <div className="nav-overlay" onClick={() => setMenuOpen(false)} />}
      
      <Link to="/" className="logo">
        <img src="/assets/logo.webp" alt="Ironwood" className="logo-icon" />
        Ironwood
      </Link>
      
      <button 
        className={`hamburger-btn ${menuOpen ? 'open' : ''}`} 
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle Navigation"
        aria-expanded={menuOpen}
        aria-controls="main-nav"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <nav id="main-nav" className={`main-nav ${menuOpen ? 'open' : ''}`}>
        <Link to="/" className={location.pathname === '/' ? 'active' : ''} onClick={() => setMenuOpen(false)}>{t('nav-home')}</Link>
        <Link to="/gallery" className={location.pathname === '/gallery' ? 'active' : ''} onClick={() => setMenuOpen(false)}>{t('nav-gallery')}</Link>
        <Link to="/contact" className={location.pathname === '/contact' ? 'active' : ''} onClick={() => setMenuOpen(false)}>{t('nav-contact')}</Link>
        <div className="lang-toggle">
          <button className={lang === 'en' ? 'active' : ''} onClick={() => { setLang('en'); setMenuOpen(false); }}>EN</button>
          <button className={lang === 'ge' ? 'active' : ''} onClick={() => { setLang('ge'); setMenuOpen(false); }}>GE</button>
        </div>
      </nav>
    </header>
  );
}

function MainLayout() {
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '/index.html' || location.pathname === '/en';

  return (
    <div className={isHome ? 'app-home' : 'app-inner'}>
      {/* Background image — always present so mobile home has it too */}
      <div className="bg-layer" />

      {/* Structured data */}
      <LocalBusinessSchema />
      <BreadcrumbSchema />

      {/* Header always first in DOM */}
      <Header />

      {/* 3D model only on home, sits below content but above background */}
      {isHome && (
        <div className="model-bg">
          <model-viewer
            src="/assets/gazebo.glb"
            camera-controls
            auto-rotate
            rotation-per-second="20deg"
            shadow-intensity="1"
            exposure="0.8"
            environment-image="neutral"
            alt="Custom Gazebo 3D Model"
            style={{ width: '100%', height: '100%', background: 'transparent' }}
          ></model-viewer>
        </div>
      )}

      {/* Page content */}
      <div className="page-content" style={{ touchAction: 'pan-y' }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <Router>
        <ScrollToTop />
        <MainLayout />
      </Router>
    </LanguageProvider>
    </HelmetProvider>
  );
}


